import { useEffect } from 'react';

/**
 * Keyboard operability for the custom account / vehicle-actions menus. While the
 * menu is open: Escape closes it and returns focus to its trigger; ArrowDown /
 * ArrowUp rove focus across the open `role="menuitem"` items (clamped at the
 * ends). Additive to the existing pointer open / select / outside-click flow.
 *
 * `triggerLabel` is the trigger's `aria-label` ("Account menu" / "More actions"),
 * used to restore focus without threading a ref through Tamagui. Only one custom
 * `role="menu"` surface is ever open at a time, so it is resolved from the DOM.
 */
export function useMenuKeyboard(open: boolean, close: () => void, triggerLabel: string): void {
  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event: KeyboardEvent): void {
      const menu = document.querySelector<HTMLElement>('[role="menu"]');
      if (!menu) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        focusTrigger(triggerLabel);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      roveFocus(menu, event.key === 'ArrowDown' ? 1 : -1);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, close, triggerLabel]);
}

function focusTrigger(label: string): void {
  document.querySelector<HTMLElement>(`[aria-label="${label}"]`)?.focus();
}

function roveFocus(menu: HTMLElement, delta: number): void {
  const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  if (items.length === 0) return;
  const current = items.indexOf(document.activeElement as HTMLElement);
  items[nextRovingIndex(current, delta, items.length)]?.focus();
}

/**
 * Pure roving-focus index math. From an unfocused state (`currentIndex < 0`),
 * ArrowDown (`delta > 0`) lands on the first item and ArrowUp on the last;
 * otherwise the move is clamped to the list ends (no wrap-around). Callers guard
 * `length > 0`.
 */
export function nextRovingIndex(currentIndex: number, delta: number, length: number): number {
  if (currentIndex < 0) return delta > 0 ? 0 : length - 1;
  return Math.max(0, Math.min(currentIndex + delta, length - 1));
}
